import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import { trackBeginTrial } from '@/lib/analytics';
import { trackEvent, FUNNEL_EVENTS } from '@/lib/analytics';

type SessionSnapshot = {
  status: string;
  plan: string | null;
  priceIdKey: string | null;
  value: number;
  currency: string;
  trialEnd: number | null;
  transactionId: string;
};

/**
 * /trial-started — shown after a Stripe checkout that started a trial.
 *
 * Fires begin_trial GA4 event and Umami PAID_ACTIVATION marker. The canonical
 * Google Ads conversion for trial start is uploaded server-side from the
 * stripe-webhook (PR 2) so we have gclid attribution even if the user closes
 * the tab before this page renders.
 *
 * Idempotent via profiles.trial_tracked_at. Flag is set before firing.
 */
const TrialStarted = () => {
  const navigate = useNavigate();
  const { user, fetchSubscriptionStatus } = useAuth();
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      logger.warn('TrialStarted: missing session_id in URL');
      setError('Missing checkout session. Redirecting…');
      const timer = setTimeout(() => navigate('/', { replace: true }), 1500);
      return () => clearTimeout(timer);
    }

    let cancelled = false;

    (async () => {
      try {
        const { data: verifyData, error: verifyErr } = await supabase.functions.invoke(
          'verify-checkout-session',
          { body: { session_id: sessionId } },
        );
        if (cancelled) return;
        if (verifyErr) {
          logger.error('TrialStarted: verify-checkout-session failed', verifyErr);
          setError('Could not verify your checkout.');
          return;
        }
        const data = verifyData as SessionSnapshot;
        setSnapshot(data);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;

        const { data: profile, error: profileErr } = await db
          .from('profiles')
          .select('trial_tracked_at')
          .eq('id', user.id)
          .single();
        if (cancelled) return;
        if (profileErr) {
          logger.error('TrialStarted: profile read failed', profileErr);
          return;
        }

        if (!profile?.trial_tracked_at) {
          const { error: updateErr } = await db
            .from('profiles')
            .update({ trial_tracked_at: new Date().toISOString() })
            .eq('id', user.id);
          if (updateErr) {
            logger.error('TrialStarted: failed to set trial_tracked_at; aborting fire', updateErr);
            return;
          }
          trackBeginTrial({ value: data.value, currency: data.currency });
          trackEvent(FUNNEL_EVENTS.PAID_ACTIVATION, { tier: data.priceIdKey ?? 'unknown' });
        }

        // Refresh subscription state so the dashboard reflects the new tier.
        // The stripe-webhook has already written org billing state by now.
        fetchSubscriptionStatus();
      } catch (err) {
        if (cancelled) return;
        logger.error('TrialStarted: unexpected error', err);
        setError('Something went wrong.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, navigate, fetchSubscriptionStatus]);

  const onContinue = () => {
    navigate('/dashboard', { replace: true });
  };

  const trialEndText = snapshot?.trialEnd
    ? new Date(snapshot.trialEnd * 1000).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-grow container mx-auto px-4 py-12 flex items-center justify-center">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">Your trial is live</CardTitle>
            <CardDescription>
              {snapshot
                ? trialEndText
                  ? `You have full access until ${trialEndText}. We'll only charge you if you continue after your trial.`
                  : `You have full access for the length of your trial. We'll only charge you if you continue.`
                : error ?? 'Confirming your trial…'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center gap-4">
            <Button onClick={onContinue} size="lg" disabled={!snapshot}>
              {snapshot ? 'Go to dashboard' : <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading…</>}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrialStarted;
